/**
 * AutoTemplate Adapter
 *
 * Converts the self-contained AutoTemplate `manifest.json` format into the
 * existing `TemplateDefinition` shape so the UI pipeline (gallery, preview,
 * template mode) works unchanged.
 */

import type { AutoTemplate } from '@/types/autoTemplate';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import {
  isTemplateCategory,
  OTHER_TEMPLATE_CATEGORY,
  type TemplateCategory,
  type TemplateDefinition,
} from '@/types/template';

// =============================================================================
// PUBLIC TYPES
// =============================================================================

/** Extra data from an AutoTemplate that doesn't map onto TemplateDefinition. */
export interface TemplateExtras {
  /** Trigger sample payload — seeded into mockedNodes[`trigger-<name>`]. */
  triggerSampleData?: TriggerMockPayload;
  /** Per-action mock state keyed by template action name. */
  mocks?: Record<string, MockNodeInfo>;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Map free-form category strings to `TemplateCategory[]` enum values.
 * Unrecognised entries are dropped; an empty result falls back to
 * `['other']` so every template lands in at least one gallery bucket.
 * Preserve recognized legacy combinations rather than applying authoring limits.
 */
function mapCategory(raw: string[]): TemplateCategory[] {
  const valid = new Set<TemplateCategory>();
  for (const entry of raw) {
    const normalised = entry.toLowerCase().trim();
    if (isTemplateCategory(normalised)) valid.add(normalised);
  }
  return valid.size > 0 ? [...valid] : [OTHER_TEMPLATE_CATEGORY];
}

/**
 * Map the template `source` field to the `TemplateSource` type.
 * "builtin" → "built-in"; anything else passes through if valid.
 */
function mapSource(raw: string | undefined): 'built-in' | 'community' {
  if (!raw || raw === 'builtin' || raw === 'built-in') return 'built-in';
  if (raw === 'community') return 'community';
  return 'built-in';
}

// =============================================================================
// ADAPTER — AutoTemplate → TemplateDefinition
// =============================================================================

/**
 * Convert a validated `AutoTemplate` to the existing `TemplateDefinition`
 * shape so it can be consumed by the gallery and template-mode pipeline.
 */
export function autoTemplateToTemplateDefinition(template: AutoTemplate): TemplateDefinition {
  const { metadata, workflow, connections } = template;

  // Build connectionRequirements array from the connections record
  const connectionRequirements = Object.entries(connections).map(([key, conn]) => ({
    connectorType: conn.connectorType,
    apiId: conn.apiId,
    referenceKey: key,
  }));

  return {
    id: metadata.id,
    name: metadata.name,
    description: metadata.description,
    category: mapCategory(metadata.category),
    tags: metadata.tags.map((t) => t.toLowerCase()),
    connectionRequirements,
    source: mapSource(metadata.source),
    author: metadata.author,
    // The AutoTemplate carries a bare definition; wrap it and pin the kind so
    // downstream consumers don't rely on their Stateful fallbacks.
    workflowDefinition: { definition: workflow.definition, kind: 'Stateful' },
    featuredConnectors: metadata.featuredConnectors,
  };
}

// =============================================================================
// EXTRAS — AutoTemplate-only data that doesn't fit in TemplateDefinition
// =============================================================================

/**
 * Extract template-specific extras (trigger sample data, mocks) that are not
 * represented in `TemplateDefinition` but are needed by template mode.
 *
 * Trigger output is reshaped into a {@link TriggerMockPayload} so it can be
 * dropped straight into `mockedNodes['trigger-<name>']`. Each entry in
 * `template.mocks` becomes a {@link MockNodeInfo} with
 * `{ mockOutput: { status, outputs: { … } } }`; actions without a mock entry
 * simply run live.
 */
export function extractTemplateExtras(template: AutoTemplate): TemplateExtras {
  const extras: TemplateExtras = {};

  // Trigger sample data — TriggerMockPayload shape (matches the API contract).
  if (template.trigger?.outputs) {
    const t = template.trigger.outputs;
    extras.triggerSampleData = {
      method: t.method,
      headers: t.headers as Record<string, string>,
      queries: t.queries as Record<string, string>,
      ...(t.body !== undefined ? { body: t.body } : {}),
    };
  }

  // Per-action mocks — emit pre-shaped MockNodeInfo entries.
  if (Object.keys(template.mocks).length > 0) {
    const mocks: Record<string, MockNodeInfo> = {};
    for (const [actionName, mock] of Object.entries(template.mocks)) {
      mocks[actionName] = {
        actionName,
        mockOutput: {
          status: mock.status,
          outputs: {
            statusCode: mock.outputs.statusCode,
            ...(mock.outputs.body !== undefined ? { body: mock.outputs.body } : {}),
            ...(mock.outputs.headers !== undefined
              ? { headers: mock.outputs.headers as Record<string, string> }
              : {}),
          },
        },
      };
    }
    extras.mocks = mocks;
  }

  return extras;
}
